import NavigationService from '@disco/state-management/NavigationService'
import type {
  ClientLibraryClient,
  ClientLibraryId,
  ClientLibraryInvite,
} from 'types/client-library'
import type { Thunk } from 'types/redux'
import api from '@disco/api/server'
import {
  CLIENT_LIBRARY_CLIENT_DELETE,
  CLIENT_LIBRARY_CLIENT_DELETE_FAIL,
  CLIENT_LIBRARY_CLIENT_DELETE_SUCCESS,
  CLIENT_LIBRARY_CLIENT_UPDATE,
  CLIENT_LIBRARY_CLIENT_UPDATE_FAIL,
  CLIENT_LIBRARY_CLIENT_UPDATE_SUCCESS,
  CLIENT_LIBRARY_CLIENTS_INVITE,
  CLIENT_LIBRARY_CLIENTS_INVITE_FAIL,
  CLIENT_LIBRARY_CLIENTS_INVITE_SUCCESS,
  CLIENT_LIBRARY_CLIENTS_LOAD,
  CLIENT_LIBRARY_CLIENTS_LOAD_FAIL,
  CLIENT_LIBRARY_CLIENTS_LOAD_SUCCESS,
  CLIENT_LIBRARY_CLIENTS_RESET_ERRORS,
  CLIENT_LIBRARY_INVITE_RESEND,
  CLIENT_LIBRARY_INVITE_RESEND_FAIL,
  CLIENT_LIBRARY_INVITE_RESEND_SUCCESS,
} from '@disco/constants/ActionTypes'
import { parseServerError } from '@disco/utils/captureError'
import { pushUINotification } from '@disco/utils/notifications'
import { plural } from '@disco/utils/string-utils'
/**
 * Invites clients to a client library or resends existing invitations.
 * @param libraryId - ID of the client library to invite clients to
 * @param data - Invitation data containing client details
 * @param type - Type of action ('invite' for new invites, other for resend)
 * @returns Thunk that processes invitations and shows status notifications
 */
export function inviteClientLibraryClients(
  libraryId: ClientLibraryId,
  data: ClientLibraryInvite,
  type = 'invite',
): Thunk {
  return async dispatch => {
    dispatch({
      type:
        type === 'invite'
          ? CLIENT_LIBRARY_CLIENTS_INVITE
          : CLIENT_LIBRARY_INVITE_RESEND,
      libraryId,
    })

    try {
      const response = await api.inviteClientLibraryClients(libraryId, data)
      const failedInvites = response
        .filter(invite => invite.status === 'already_invited')
        .map(invite => invite.email)
      const successfullySentInvites = response.length - failedInvites.length

      if (failedInvites.length > 0) {
        pushUINotification({
          message: `Each client can only be invited once within 24 hours. Please wait until 24 hours since the first invite to re-send the invitation to ${failedInvites.join(
            ', ',
          )}.`,
          level: 'error',
        })
      }

      if (successfullySentInvites > 0) {
        pushUINotification({
          message: `${plural(
            successfullySentInvites,
            'Client',
            'Clients',
          )} invited!`,
          level: 'success',
        })
      }

      dispatch({
        type:
          type === 'invite'
            ? CLIENT_LIBRARY_CLIENTS_INVITE_SUCCESS
            : CLIENT_LIBRARY_INVITE_RESEND_SUCCESS,
        libraryId,
        response,
      })
      NavigationService.push(`/catalogs/${libraryId}/clients`)
    } catch (error) {
      dispatch({
        type:
          type === 'invite'
            ? CLIENT_LIBRARY_CLIENTS_INVITE_FAIL
            : CLIENT_LIBRARY_INVITE_RESEND_FAIL,
        libraryId,
        error: error.body,
      })
    }
  }
}
/**
 * Resends an invitation to a specific client for a client library.
 * @param libraryId - ID of the client library to resend invitation for
 * @param clientId - ID of the client to resend invitation to
 * @returns Thunk that resends invitation and shows success notification
 */
export function resendClientLibraryInvite(
  libraryId: ClientLibraryId,
  clientId: number,
): Thunk {
  return async dispatch => {
    dispatch({
      type: CLIENT_LIBRARY_INVITE_RESEND,
    })

    try {
      await api.resendClientLibraryInvite(libraryId, clientId)
      dispatch({
        type: CLIENT_LIBRARY_INVITE_RESEND_SUCCESS,
      })
      pushUINotification({
        message: 'Clients invited!',
        level: 'success',
      })
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_INVITE_RESEND_FAIL,
        error: error.body,
      })
    }
  }
}
/**
 * Resets error states for client library clients.
 * @param libraryId - ID of the client library to reset errors for
 * @returns Action object to reset client error states
 */
export function clientLibraryClientsResetErrors(libraryId: ClientLibraryId): {
  libraryId: ClientLibraryId
  type: string
} {
  return {
    type: CLIENT_LIBRARY_CLIENTS_RESET_ERRORS,
    libraryId,
  }
}
/**
 * Loads all clients for a specific client library.
 * @param libraryId - ID of the client library to load clients for
 * @returns Thunk that loads clients and handles success/error states
 */
export function clientLibraryClientsLoad(libraryId: ClientLibraryId): Thunk {
  return async dispatch => {
    dispatch({
      type: CLIENT_LIBRARY_CLIENTS_LOAD,
      libraryId,
    })

    try {
      const response = await api.getClientLibraryClients(libraryId)
      dispatch({
        type: CLIENT_LIBRARY_CLIENTS_LOAD_SUCCESS,
        libraryId,
        response,
      })
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_CLIENTS_LOAD_FAIL,
        libraryId,
        error,
      })
      pushUINotification({
        message:
          'There was an error loading library clients! Please try again or contact us.',
        level: 'error',
      })
    }
  }
}
/**
 * Deletes a client from a client library.
 * @param libraryId - ID of the client library to delete client from
 * @param clientId - ID of the client to delete
 * @returns Thunk that deletes client and handles success/error states
 */
export function clientLibraryClientDelete(
  libraryId: ClientLibraryId,
  clientId: number,
): Thunk {
  return async dispatch => {
    try {
      dispatch({
        type: CLIENT_LIBRARY_CLIENT_DELETE,
        libraryId,
        clientId,
      })
      await api.deleteClientLibraryClient(libraryId, clientId)
      dispatch({
        type: CLIENT_LIBRARY_CLIENT_DELETE_SUCCESS,
        libraryId,
        clientId,
      })
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_CLIENT_DELETE_FAIL,
        libraryId,
        error,
      })
    }
  }
}
/**
 * Updates client information in a client library.
 * @param libraryId - ID of the client library containing the client
 * @param clientId - ID of the client to update
 * @param data - Partial client data to update
 * @returns Thunk that updates client and shows success/error notifications
 */
export function clientLibraryClientUpdate(
  libraryId: ClientLibraryId,
  clientId: number,
  data: Partial<ClientLibraryClient>,
): Thunk {
  return async dispatch => {
    dispatch({
      type: CLIENT_LIBRARY_CLIENT_UPDATE,
      libraryId,
      clientId,
    })

    try {
      const result = await api.updateClientLibraryClient(
        libraryId,
        clientId,
        data,
      )
      dispatch({
        type: CLIENT_LIBRARY_CLIENT_UPDATE_SUCCESS,
        libraryId,
        clientId,
        data: result,
      })
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_CLIENT_UPDATE_FAIL,
        libraryId,
        clientId,
        error: error.body,
      })
      pushUINotification({
        message: parseServerError(error.body),
        level: 'error',
      })
    }
  }
}
